import React, { useState } from 'react'

import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import CardActions from '@mui/material/CardActions'
import Button from '@mui/material/Button'
import LaunchIcon from '@mui/icons-material/Launch'

import { DESCRIPTION_CLAMP_SX } from '../shared/ResourceDescription'

// Cards clamp to 3 lines with no toggle; the full text lives in the detail modal.
const DESCRIPTION_SX = { ...DESCRIPTION_CLAMP_SX, WebkitLineClamp: 3, overflowWrap: 'anywhere', mt: 1 }

/**
 * Generic resource card that renders both a mobile and a desktop variant.
 *
 * Props:
 *   Icon                – MUI icon component class (e.g. PlayArrowIcon)
 *   cardStyle           – Style object (e.g. VideoCardStyle). Every BaseCardStyle key, including
 *                         buttonContainer, is required on both viewports (each style is a structuredClone).
 *   modal               – Pre-rendered modal element. Rendered inside BOTH cards'
 *                         CardActions, first, before the view control - the enclosing
 *                         grid item is display:flex, so a fragment-level modal becomes
 *                         a sibling box floating beside the card (issue #613).
 *   title               – Title string to display
 *   thumbnail           – Optional image URL rendered in place of the type icon.
 *                         Falls back to the Icon if absent or if the image errors.
 *   date                – Date string to display
 *   dateLabel           – Label prefix for the date, e.g. "Published:" or "Updated:"
 *   description         – Optional description text, clamped to three lines (no expand toggle)
 *   content_tags        – Array of content tag strings
 *   medical_specialty_tags – Array of medical specialty tag strings
 *   is_seminal_work     – Boolean; renders a "Seminal" badge if true
 *   onOpen              – Callback invoked when "View Resource" is clicked
 *   buttonText          – Label for the view button (default: "View Resource")
 *   showButton          – Whether to render the view button (default: true)
 *   mobileBodyExtra     – Optional JSX rendered inside the mobile card body (e.g. ratings)
 *   desktopBodyExtra    – Optional JSX rendered before the title in the desktop card
 *   mobileExtraActions  – Optional extra action elements in the mobile card
 *   desktopExtraActions – Optional extra action elements in the desktop card
 *   viewControl         – Optional render-prop (styleProps) => JSX that replaces the default
 *                         "View Resource" button entirely (e.g. a dropdown menu). Called once
 *                         per viewport with that viewport's button style props.
 */
const ResourceCard = ({
    Icon,
    cardStyle,
    modal,
    title,
    thumbnail,
    date,
    dateLabel = 'Published:',
    description,
    content_tags,
    medical_specialty_tags,
    is_seminal_work,
    onOpen,
    buttonText = 'View Resource',
    showButton = true,
    mobileBodyExtra,
    desktopBodyExtra,
    mobileExtraActions,
    desktopExtraActions,
    viewControl,
}) => {
    // Keyed by src so a stale failure doesn't suppress a new thumbnail if the
    // prop changes. Shared across viewports: both imgs load the same URL, so
    // one error means both should fall back.
    const [failedSrc, setFailedSrc] = useState(null)
    const showThumbnail = Boolean(thumbnail) && failedSrc !== thumbnail

    const visual = (iconStyle) =>
        showThumbnail
            ? <img
                src={thumbnail}
                alt={title}
                loading='lazy'
                // Cross-origin thumbnail hosts need no Referer; don't leak the page URL.
                // The T-CAIREM poster uses the same policy (#430).
                referrerPolicy='no-referrer'
                onError={() => setFailedSrc(thumbnail)}
                style={iconStyle.thumbnail}
              />
            : <Icon color={iconStyle.icon.color} sx={iconStyle.icon} />

    const TagBadges = ({ tags, badgeStyle }) =>
        tags && tags.length > 0
            ? <Box sx={{ mt: 1 }}>{tags.map(tag => <Chip key={tag} label={tag} sx={badgeStyle} />)}</Box>
            : null

    return (
        <>
            {/* Mobile card */}
            <Card sx={cardStyle.mobile.card.card} variant={cardStyle.mobile.card.variant}>
                <Box sx={cardStyle.mobile.icon.box}>
                    {visual(cardStyle.mobile.icon)}
                </Box>
                <Box sx={cardStyle.mobile.body.container}>
                    <CardContent sx={cardStyle.mobile.body.meta}>
                        <Box sx={cardStyle.mobile.body.text}>
                            <Typography {...cardStyle.mobile.body.title.props} sx={cardStyle.mobile.body.title.style}>
                                {title}
                            </Typography>
                            {mobileBodyExtra}
                        </Box>
                        <Typography {...cardStyle.mobile.body.audience}>
                            {dateLabel} {date}
                        </Typography>
                        {description?.trim() && (
                            <Typography variant='body2' color='text.secondary' sx={DESCRIPTION_SX}>{description}</Typography>
                        )}
                        <TagBadges tags={content_tags} badgeStyle={cardStyle.mobile.body.badge} />
                        <TagBadges tags={medical_specialty_tags} badgeStyle={cardStyle.mobile.body.badge} />
                        {is_seminal_work && (
                            <Box sx={{ mt: 1 }}>
                                <Chip label="Seminal" sx={cardStyle.mobile.body.badge} />
                            </Box>
                        )}
                    </CardContent>
                    <CardActions sx={cardStyle.mobile.buttonContainer.style}>
                        {modal}
                        {viewControl
                            ? viewControl({ buttonProps: cardStyle.mobile.button.props, sx: cardStyle.mobile.button.style })
                            : showButton && (
                                <Button {...cardStyle.mobile.button.props} sx={cardStyle.mobile.button.style} onClick={onOpen}>
                                    {buttonText}&nbsp;<LaunchIcon fontSize='small' />
                                </Button>
                            )}
                        {mobileExtraActions}
                    </CardActions>
                </Box>
            </Card>

            {/* Desktop card */}
            <Card sx={cardStyle.desktop.card.card} variant={cardStyle.desktop.card.variant}>
                <Box sx={cardStyle.desktop.icon.box}>
                    {visual(cardStyle.desktop.icon)}
                </Box>
                <CardContent sx={cardStyle.desktop.body.cardContent}>
                    {desktopBodyExtra}
                    <Box>
                        <Typography {...cardStyle.desktop.body.title.props} sx={cardStyle.desktop.body.title.style}>
                            {title}
                        </Typography>
                    </Box>
                    <Typography {...cardStyle.desktop.body.audience}>
                        {dateLabel} {date}
                    </Typography>
                    {description?.trim() && (
                        <Typography variant='body2' color='text.secondary' sx={DESCRIPTION_SX}>{description}</Typography>
                    )}
                    <TagBadges tags={content_tags} badgeStyle={cardStyle.desktop.body.badge} />
                    <TagBadges tags={medical_specialty_tags} badgeStyle={cardStyle.desktop.body.badge} />
                    {is_seminal_work && (
                        <Box sx={{ mt: 1 }}>
                            <Chip label="Seminal" sx={cardStyle.desktop.body.badge} />
                        </Box>
                    )}
                </CardContent>
                <CardActions sx={cardStyle.desktop.buttonContainer.style}>
                    {modal}
                    {viewControl
                        ? viewControl({ buttonProps: cardStyle.desktop.button.props, sx: cardStyle.desktop.button.style })
                        : showButton && (
                            <Button {...cardStyle.desktop.button.props} sx={cardStyle.desktop.button.style} onClick={onOpen}>
                                {buttonText}&nbsp;<LaunchIcon fontSize='small' />
                            </Button>
                        )}
                    {desktopExtraActions}
                </CardActions>
            </Card>
        </>
    )
}

export default ResourceCard
